import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Banknote, Loader2, Check, RefreshCw, Zap } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  Unallocated: "bg-amber-50 text-amber-700 border-amber-200",
  "Partially Allocated": "bg-blue-50 text-blue-700 border-blue-200",
  "Fully Allocated": "bg-green-50 text-green-700 border-green-200",
};
const methods = ["EFT", "Cash", "Card", "Cheque", "Other"];
const fmt = (v) => `R${(parseFloat(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

export default function CustomerPaymentsPage() {
  const { id } = useParams();
  if (id === "new") return <CreatePayment />;
  if (id) return <PaymentDetail payId={id} />;
  return <PaymentList />;
}

function PaymentList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/customer-payments?status=${filter}&limit=50`).then(r => { setItems(r.data.payments); setTotal(r.data.total); }).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="customer-payments-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading font-bold text-2xl text-[#1F2937]">Customer Payments</h1><p className="text-sm text-gray-500 mt-1">{total} payment{total !== 1 ? "s" : ""}</p></div>
        <Button data-testid="record-payment-btn" onClick={() => navigate("/customer-payments/new")} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Plus className="w-4 h-4 mr-1.5" />Record Payment</Button>
      </div>
      <div className="flex gap-2 mb-4">
        {["", "Unallocated", "Partially Allocated", "Fully Allocated"].map(s => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className={`rounded-sm text-xs ${filter === s ? "bg-[#E63946] text-white" : ""}`}>{s || "All"}</Button>
        ))}
      </div>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : items.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><Banknote className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No payments recorded</p></CardContent></Card>
      ) : (
        <Card className="border rounded-sm shadow-sm"><div className="overflow-x-auto">
          <table data-testid="customer-payments-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Number</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Customer</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Method</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Amount</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Allocated</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Unallocated</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
            </tr></thead>
            <tbody>{items.map(p => (
              <tr key={p.payment_id} data-testid={`pay-row-${p.payment_id}`} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/customer-payments/${p.payment_id}`)}>
                <td className="p-3 font-medium text-[#E63946]">{p.payment_number}</td>
                <td className="p-3 text-gray-600">{p.payment_date}</td>
                <td className="p-3 text-gray-700">{p.customer_name}</td>
                <td className="p-3"><Badge className="rounded-full text-xs bg-gray-100 text-gray-600">{p.payment_method}</Badge></td>
                <td className="p-3 text-right font-mono">{fmt(p.amount)}</td>
                <td className="p-3 text-right font-mono text-green-600">{fmt(p.allocated_amount)}</td>
                <td className="p-3 text-right font-mono text-amber-600">{fmt(p.unallocated_amount)}</td>
                <td className="p-3"><Badge className={`rounded-full text-xs border ${statusColors[p.status] || "bg-gray-100"}`}>{p.status}</Badge></td>
              </tr>
            ))}</tbody>
          </table>
        </div></Card>
      )}
    </div>
  );
}

function CreatePayment() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ customer_id: "", amount: "", payment_method: "EFT", payment_date: new Date().toISOString().split("T")[0], reference: "", bank_account: "", notes: "" });
  const [invoices, setInvoices] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/customers?limit=500").then(r => setCustomers(r.data.customers || [])); }, []);
  useEffect(() => {
    if (form.customer_id) { api.get(`/customers/${form.customer_id}/outstanding-invoices`).then(r => { setInvoices(r.data.invoices || []); setAllocations({}); }); }
    else { setInvoices([]); setAllocations({}); }
  }, [form.customer_id]);

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const paymentAmount = parseFloat(form.amount) || 0;
  const unallocated = paymentAmount - totalAllocated;

  const autoAllocate = () => {
    if (paymentAmount <= 0) { toast.error("Enter payment amount first"); return; }
    let remaining = paymentAmount;
    const newAllocs = {};
    for (const inv of invoices) {
      if (remaining <= 0.01) break;
      const bal = parseFloat(inv.balance) || 0;
      const alloc = Math.min(remaining, bal);
      if (alloc > 0) { newAllocs[inv.invoice_id] = alloc.toFixed(2); remaining -= alloc; }
    }
    setAllocations(newAllocs);
    toast.success("Auto-allocated to earliest invoices");
  };

  const clearAllocs = () => { setAllocations({}); };

  const submit = async () => {
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (paymentAmount <= 0) { toast.error("Enter payment amount"); return; }
    if (!form.payment_method) { toast.error("Select payment method"); return; }
    if (totalAllocated > paymentAmount + 0.01) { toast.error("Allocations exceed payment amount"); return; }
    const allocs = Object.entries(allocations).filter(([, v]) => parseFloat(v) > 0).map(([id, v]) => ({ invoice_id: id, amount: parseFloat(v) }));
    setSaving(true);
    try {
      const r = await api.post("/customer-payments", { ...form, amount: paymentAmount, allocations: allocs });
      toast.success(`Payment ${r.data.payment_number} recorded`);
      navigate(`/customer-payments/${r.data.payment_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="create-payment-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/customer-payments")} className="rounded-sm"><ArrowLeft className="w-5 h-5" /></Button>
        <h1 className="font-heading font-bold text-2xl text-[#1F2937]">Record Customer Payment</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Customer *</label>
          <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v})}>
            <SelectTrigger data-testid="pay-customer-select" className="rounded-sm"><SelectValue placeholder="Select customer..." /></SelectTrigger>
            <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Amount Received *</label>
          <Input data-testid="pay-amount" type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" className="rounded-sm" min="0" step="0.01" />
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Payment Method *</label>
          <Select value={form.payment_method} onValueChange={v => setForm({...form, payment_method: v})}>
            <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{methods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Date *</label>
          <Input type="date" value={form.payment_date} onChange={e => setForm({...form, payment_date: e.target.value})} className="rounded-sm" />
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Reference</label>
          <Input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} placeholder="Transaction ID, cheque number, etc." className="rounded-sm" />
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Notes</label>
          <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes" className="rounded-sm" />
        </div>
      </div>
      {form.customer_id && (
        <Card className="border rounded-sm mb-6"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-sm text-[#1F2937]">Allocate to Invoices</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={autoAllocate} className="rounded-sm h-8 text-xs"><Zap className="w-3 h-3 mr-1" />Auto-Allocate</Button>
              <Button size="sm" variant="outline" onClick={clearAllocs} className="rounded-sm h-8 text-xs">Clear</Button>
            </div>
          </div>
          {invoices.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No outstanding invoices</p> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="p-2 text-left text-xs text-gray-500">Invoice</th><th className="p-2 text-left text-xs text-gray-500">Date</th><th className="p-2 text-left text-xs text-gray-500">Due</th><th className="p-2 text-right text-xs text-gray-500">Total</th><th className="p-2 text-right text-xs text-gray-500">Balance</th><th className="p-2 w-32 text-xs text-gray-500">Allocate</th></tr></thead>
              <tbody>{invoices.map(inv => (
                <tr key={inv.invoice_id} className="border-b border-gray-100">
                  <td className="p-2 font-medium text-[#E63946]">{inv.invoice_number}</td>
                  <td className="p-2 text-gray-600">{inv.invoice_date}</td>
                  <td className="p-2 text-gray-600">{inv.due_date}</td>
                  <td className="p-2 text-right font-mono">{fmt(inv.total)}</td>
                  <td className="p-2 text-right font-mono">{fmt(inv.balance)}</td>
                  <td className="p-2"><Input type="number" value={allocations[inv.invoice_id] || ""} onChange={e => setAllocations({...allocations, [inv.invoice_id]: e.target.value})} className="rounded-sm h-8 text-sm text-right" placeholder="0.00" min="0" max={inv.balance} step="0.01" /></td>
                </tr>
              ))}</tbody>
              <tfoot>
                <tr className="border-t"><td colSpan="5" className="p-2 text-right text-xs text-gray-500">Total Allocated:</td><td className="p-2 text-right font-mono font-medium text-green-600">{fmt(totalAllocated)}</td></tr>
                <tr><td colSpan="5" className="p-2 text-right text-xs text-gray-500">Unallocated (on account):</td><td className={`p-2 text-right font-mono font-medium ${unallocated < 0 ? "text-red-500" : "text-amber-600"}`}>{fmt(Math.max(0, unallocated))}</td></tr>
              </tfoot>
            </table>
          )}
          {unallocated > 0.01 && <p className="text-xs text-amber-500 mt-2">Unallocated amounts will be kept on account for future invoices.</p>}
        </CardContent></Card>
      )}
      <div className="flex gap-3">
        <Button data-testid="save-payment-btn" onClick={submit} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}Record Payment</Button>
        <Button variant="outline" onClick={() => navigate("/customer-payments")} className="rounded-sm">Cancel</Button>
      </div>
    </div>
  );
}

function PaymentDetail({ payId }) {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allocDialog, setAllocDialog] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [allocForm, setAllocForm] = useState({ invoice_id: "", amount: 0 });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/customer-payments/${payId}`).then(r => {
      setP(r.data);
      if (r.data.customer_id) api.get(`/customers/${r.data.customer_id}/outstanding-invoices`).then(r2 => setInvoices(r2.data.invoices || []));
    }).catch(() => toast.error("Not found")).finally(() => setLoading(false));
  }, [payId]);
  useEffect(() => { load(); }, [load]);

  const allocateManual = async () => {
    if (!allocForm.invoice_id || allocForm.amount <= 0) { toast.error("Select invoice and amount"); return; }
    setSubmitting(true);
    try {
      await api.post(`/customer-payments/${payId}/allocate`, allocForm);
      toast.success("Payment allocated");
      setAllocDialog(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  const autoAllocate = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/customer-payments/${payId}/auto-allocate`);
      toast.success(`Auto-allocated to ${r.data.allocated_count} invoice(s): ${fmt(r.data.total_allocated)}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  if (loading) return <div className="animate-pulse h-60 bg-gray-100 rounded-sm" />;
  if (!p) return <p>Not found</p>;

  return (
    <div data-testid="payment-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/customer-payments")} className="rounded-sm"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1"><h1 className="font-heading font-bold text-2xl text-[#1F2937]">{p.payment_number}</h1><p className="text-sm text-gray-500">{p.customer_name}</p></div>
        <Badge className={`rounded-full text-sm border px-3 py-1 ${statusColors[p.status] || "bg-gray-100"}`}>{p.status}</Badge>
      </div>
      {p.unallocated_amount > 0.01 && (
        <div className="flex gap-2 mb-6">
          <Button data-testid="manual-allocate-btn" size="sm" onClick={() => { setAllocForm({ invoice_id: "", amount: p.unallocated_amount }); setAllocDialog(true); }} className="bg-green-600 hover:bg-green-700 text-white rounded-sm"><Plus className="w-4 h-4 mr-1.5" />Allocate to Invoice</Button>
          <Button data-testid="auto-allocate-btn" size="sm" variant="outline" onClick={autoAllocate} disabled={submitting} className="rounded-sm"><Zap className="w-4 h-4 mr-1.5" />Auto-Allocate</Button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <Card className="border rounded-sm"><CardContent className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Total Received</p>
          <p className="text-2xl font-bold font-mono text-[#1F2937]">{fmt(p.amount)}</p>
        </CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Allocated</p>
          <p className="text-2xl font-bold font-mono text-green-600">{fmt(p.allocated_amount)}</p>
        </CardContent></Card>
        <Card className="border rounded-sm border-amber-200"><CardContent className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Unallocated</p>
          <p className="text-2xl font-bold font-mono text-amber-600">{fmt(p.unallocated_amount)}</p>
        </CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4 space-y-1.5">
          <p className="text-xs text-gray-500 uppercase font-semibold">Details</p>
          <p className="text-sm"><span className="text-gray-500">Date:</span> {p.payment_date}</p>
          <p className="text-sm"><span className="text-gray-500">Method:</span> {p.payment_method}</p>
          {p.reference && <p className="text-sm"><span className="text-gray-500">Ref:</span> {p.reference}</p>}
        </CardContent></Card>
      </div>
      {p.allocations && p.allocations.length > 0 && (
        <Card className="border rounded-sm mb-6"><CardContent className="p-4">
          <h3 className="font-heading font-semibold text-sm mb-3">Allocations</h3>
          <table className="w-full text-sm"><thead><tr className="border-b"><th className="p-2 text-left text-xs text-gray-500">Invoice</th><th className="p-2 text-right text-xs text-gray-500">Amount</th></tr></thead>
            <tbody>{p.allocations.map(a => <tr key={a.allocation_id} className="border-b border-gray-100"><td className="p-2 text-[#E63946] cursor-pointer" onClick={() => navigate(`/invoices/${a.invoice_id}`)}>{a.invoice_number}</td><td className="p-2 text-right font-mono">{fmt(a.amount)}</td></tr>)}</tbody>
          </table>
        </CardContent></Card>
      )}
      {p.notes && <Card className="border rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Notes</p><p className="text-sm text-gray-600">{p.notes}</p></CardContent></Card>}
      {/* Allocate Dialog */}
      <Dialog open={allocDialog} onOpenChange={setAllocDialog}>
        <DialogContent className="rounded-sm"><DialogHeader><DialogTitle>Allocate to Invoice</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 mb-3">Unallocated: <strong>{fmt(p.unallocated_amount)}</strong></p>
          <div className="space-y-3">
            <div><label className="text-xs font-medium text-gray-500 mb-1 block">Invoice *</label>
              <Select value={allocForm.invoice_id || "none"} onValueChange={v => { const inv = invoices.find(i => i.invoice_id === v); setAllocForm({...allocForm, invoice_id: v === "none" ? "" : v, amount: inv ? Math.min(p.unallocated_amount, inv.balance) : 0}); }}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select invoice..." /></SelectTrigger>
                <SelectContent>{invoices.map(i => <SelectItem key={i.invoice_id} value={i.invoice_id}>{i.invoice_number} — Balance: {fmt(i.balance)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-medium text-gray-500 mb-1 block">Amount *</label><Input type="number" value={allocForm.amount} onChange={e => setAllocForm({...allocForm, amount: parseFloat(e.target.value) || 0})} className="rounded-sm" step="0.01" min="0" max={p.unallocated_amount} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAllocDialog(false)} className="rounded-sm">Cancel</Button><Button onClick={allocateManual} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Allocate"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
